import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePrivateResearchGeneration } from '@/hooks/private-research-generator/usePrivateResearchGeneration';
import { useUser } from '@/hooks/auth/use-user';
import { ExtractedResearch } from './useRecommendationDetection';

interface AgentChatResearchParams {
  research: ExtractedResearch;
  messageId: string;
  // goalId removed - no longer required for private research
}

/**
 * Hook for generating research from agent chat recommendations
 * Now routes to private research library instead of goal-based research
 * Simplified flow - no more planner_suggested_research complexity
 */
export const useAgentChatResearchGeneration = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { mutateAsync: generatePrivateResearch } = usePrivateResearchGeneration();

  return useMutation({
    mutationFn: async ({ research, messageId }: AgentChatResearchParams) => {
      console.log('[AgentChatResearch] Starting private research generation for:', research.title);
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Generate research directly in private library (no project context)
      const generatedResearch = await generatePrivateResearch({
        researchType: 'General Research',
        researchPrompt: research.title,
        // No workbook_id - stays in private library
        selectedFilters: {
          origin_type: 'agent_chat',
          origin_id: messageId,
          origin_title: research.title,
          origin_description: research.description
        }
      });

      console.log('[AgentChatResearch] Private research generated successfully:', generatedResearch.id);

      return {
        generatedResearch
      };
    },
    onSuccess: (data, variables) => {
      console.log('[AgentChatResearch] Success, invalidating private research queries');
      
      // Invalidate private research library queries
      queryClient.invalidateQueries({ 
        queryKey: ['private-research-library'],
        exact: false 
      });
      
      queryClient.invalidateQueries({ 
        queryKey: ['private-research-library-data'],
        exact: false 
      });
    },
    onError: (error) => {
      console.error('[AgentChatResearch] Error:', error);
    },
  });
}; 